// AsVG-Präventionszeit: Soll/Ist-Vergleich für arbeitsmedizinische Betreuungsstunden
// Basis: AUVA-Empfehlungen & AMR 4 (Arbeitsmedizinische Regel 4)

import { db } from '../db/schema';

// Soll-Stunden pro Jahr nach Branche (AUVA-Vorgaben)
// Quelle: AMR 4, §79 ASchG, AUVA-Leitfaden Arbeitsmedizin
const BRANCHE_SOLL_STUNDEN: Record<string, number> = {
  'Metallerzeugung & -bearbeitung': 3.0,
  'Metallerzeugung':                3.0,
  'Holzverarbeitung':               4.0,
  'Chemische Industrie':            5.0,
  'Bauwirtschaft':                  3.0,
  'Seilbahnen & Bergbahnen':        2.5,
  'Gastronomie & Tourismus':        2.0,
  'Handel':                         1.5,
  'Bürotätigkeit':                  1.0,
  'Gesundheitswesen':               2.5,
  'Transport & Logistik':           3.0,
  'Landwirtschaft':                 2.0,
  'Elektrotechnik':                 2.5,
  'Kunststoffverarbeitung':         3.0,
  'Druckerei':                      2.5,
};

const SOLL_DEFAULT = 2.0; // Stunden/Jahr pro Mitarbeiter falls Branche unbekannt

export function sollStundenFuerBranche(branche?: string): number {
  if (!branche) { return SOLL_DEFAULT; }
  // Exakter Match
  if (branche in BRANCHE_SOLL_STUNDEN) { return BRANCHE_SOLL_STUNDEN[branche] ?? SOLL_DEFAULT; }
  // Partial Match (z.B. "Chemische Industrie & mehr" → "Chemische Industrie")
  const key = Object.keys(BRANCHE_SOLL_STUNDEN).find(k => branche.includes(k) || k.includes(branche));
  return key !== undefined ? (BRANCHE_SOLL_STUNDEN[key] ?? SOLL_DEFAULT) : SOLL_DEFAULT;
}

// Anteiliger Soll-Wert für das laufende Jahr (proportional zu vergangenen Tagen)
export function anteiligenSoll(sollJahr: number, mitarbeiterzahl: number): number {
  const now       = new Date();
  const startYear = new Date(now.getFullYear(), 0, 1);
  const dayOfYear = Math.ceil((now.getTime() - startYear.getTime()) / 86_400_000);
  const daysInYear = (now.getFullYear() % 4 === 0 && (now.getFullYear() % 100 !== 0 || now.getFullYear() % 400 === 0)) ? 366 : 365;
  return sollJahr * mitarbeiterzahl * (dayOfYear / daysInYear);
}

export async function aggregateIstStunden(mandantId?: string, year?: number): Promise<number> {
  const y = year ?? new Date().getFullYear();
  const prefix = String(y);

  let query = db.zeiteintraege.filter(z => z.datum.startsWith(prefix));
  if (mandantId !== undefined) {
    query = db.zeiteintraege
      .where('mandantId').equals(mandantId)
      .filter(z => z.datum.startsWith(prefix));
  }
  const eintraege = await query.toArray();
  return eintraege.reduce((sum, z) => sum + z.dauerMin, 0) / 60;
}

export interface PraeventionKpi {
  istStunden:  number;
  sollStunden: number;
  prozent:     number;   // 0–999
  farbe:       'green' | 'orange' | 'red';
}

export async function getPraeventionKpi(
  mandantId?: string,
  branche?: string,
  mitarbeiterzahl?: number,
): Promise<PraeventionKpi> {
  const istStunden  = await aggregateIstStunden(mandantId);
  const sollJahr    = sollStundenFuerBranche(branche);
  const ma          = mitarbeiterzahl ?? 1;
  const sollStunden = anteiligenSoll(sollJahr, ma);

  const prozent = sollStunden > 0 ? Math.round((istStunden / sollStunden) * 100) : 0;
  const farbe   = prozent >= 90 ? 'green' : prozent >= 50 ? 'orange' : 'red';

  return { istStunden, sollStunden, prozent, farbe };
}
